import type { TargetField, ColumnMapping } from '../types';

// Synonym map: lowercase variants → target field
const HEADER_SYNONYMS: Record<string, TargetField> = {
  // Name
  'coral name': 'name',
  'name': 'name',
  'common name': 'name',
  'common_name': 'name',
  'species': 'name',
  // Category
  'coral type': 'category',
  'type': 'category',
  'sps/lps': 'category',
  'category': 'category',
  // Subcategory
  'coral category': 'subcategory',
  'genus': 'subcategory',
  'subcategory': 'subcategory',
  'sub category': 'subcategory',
  // Acquisition Date
  'purchase date': 'acquisition_date',
  'date': 'acquisition_date',
  'acquired': 'acquisition_date',
  'acquisition date': 'acquisition_date',
  'acquisition_date': 'acquisition_date',
  'date purchased': 'acquisition_date',
  // Price
  'price': 'price',
  'price paid': 'price',
  'price_paid': 'price',
  'cost': 'price',
  'amount': 'price',
  // Status
  'status': 'status',
  'alive/dead': 'status',
  'alive dead': 'status',
  // Notes
  'notes': 'notes',
  'comments': 'notes',
  'note': 'notes',
  // Vendor
  'vendor': 'vendor',
  'seller': 'vendor',
  'source': 'vendor',
  'shop': 'vendor',
  'store': 'vendor',
  // Scientific Name
  'scientific name': 'scientific_name',
  'scientific_name': 'scientific_name',
  'latin name': 'scientific_name',
};

// Target fields that can only be mapped once
const UNIQUE_TARGETS: TargetField[] = [
  'name',
  'category',
  'subcategory',
  'vendor',
  'price',
  'acquisition_date',
  'status',
  'notes',
  'scientific_name',
];

export const TARGET_FIELD_OPTIONS: { value: TargetField; label: string }[] = [
  { value: 'name', label: 'Name (required)' },
  { value: 'category', label: 'Category (e.g. SPS, LPS)' },
  { value: 'subcategory', label: 'Subcategory (e.g. Acropora, Torches)' },
  { value: 'vendor', label: 'Vendor' },
  { value: 'price', label: 'Price' },
  { value: 'acquisition_date', label: 'Acquisition Date (required)' },
  { value: 'status', label: 'Status' },
  { value: 'notes', label: 'Notes' },
  { value: 'scientific_name', label: 'Scientific Name' },
  { value: 'skip', label: "Skip (don't import)" },
];

/**
 * Auto-detect column mappings from spreadsheet headers.
 * Returns a ColumnMapping for each header, with auto-detected targetField
 * or 'skip' if no match found.
 */
export function detectColumnMappings(
  headers: string[],
  rows: Record<string, string>[],
): ColumnMapping[] {
  const usedTargets = new Set<TargetField>();

  return headers.map((header) => {
    const normalized = header.toLowerCase().trim();
    let target: TargetField = 'skip';

    // Try exact match first
    if (
      HEADER_SYNONYMS[normalized] &&
      !usedTargets.has(HEADER_SYNONYMS[normalized])
    ) {
      target = HEADER_SYNONYMS[normalized];
    } else {
      // Try contains match
      for (const [synonym, field] of Object.entries(HEADER_SYNONYMS)) {
        if (
          !usedTargets.has(field) &&
          (normalized.includes(synonym) || synonym.includes(normalized))
        ) {
          target = field;
          break;
        }
      }
    }

    if (target !== 'skip' && UNIQUE_TARGETS.includes(target)) {
      usedTargets.add(target);
    }

    // Get up to 3 sample values from the rows
    const sampleValues = rows
      .slice(0, 3)
      .map((row) => row[header] || '')
      .filter((v) => v.trim() !== '');

    return { sourceColumn: header, targetField: target, sampleValues };
  });
}

/**
 * Check which target fields are available (not yet used by other mappings).
 */
export function getAvailableTargets(
  mappings: ColumnMapping[],
  currentIndex: number,
): { value: TargetField; label: string; disabled: boolean }[] {
  const usedTargets = new Set<TargetField>();
  mappings.forEach((m, i) => {
    if (i !== currentIndex && m.targetField !== 'skip') {
      usedTargets.add(m.targetField);
    }
  });

  return TARGET_FIELD_OPTIONS.map((opt) => ({
    ...opt,
    disabled: opt.value !== 'skip' && usedTargets.has(opt.value),
  }));
}
